/** Threads の接続方法: Meta for Developers で自分のアプリを作り、ユーザートークン生成ツールで長期トークンを発行して貼る */
import {
  BrowserFrame, Callout, Checklist, Code, Mark, MockButton, MockInput, MockRow, MockSidebarLayout,
  PhoneFrame, SectionTitle, Step, Steps, Trouble, UiLabel,
} from './parts'
import { ChannelRowMock, ConnectButtonsMock, ConnectFormMock } from './postMocks'

const META_MENU = ['ダッシュボード', 'ユースケース', 'アプリの役割', 'アプリの設定', 'アプリレビュー']

export function ThreadsGuide() {
  return (
    <div className="space-y-5">
      <Callout kind="info" title="Threads は「長期アクセストークン」を1つ貼るだけです">
        <p>
          Meta（Facebook・Instagram・Threads の運営会社）の開発者サイトで、自分専用のアプリを作ります。
          自分のアカウントにだけ使うので、Meta の審査は要りません。発行したトークンは 60 日有効で、<b>期限が近づくと Genemo Post が自動で延長</b>します。
        </p>
      </Callout>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="gm-card space-y-2 p-4">
          <h3 className="text-sm font-bold">準備するもの</h3>
          <Checklist items={[
            <>投稿したい Threads アカウント（<b>公開アカウント</b>にしておく）</>,
            'Meta for Developers に登録した Facebook アカウント（初めてなら登録します。無料）',
            'パソコンのブラウザ（Threads の招待の承認は Web 版が分かりやすいです）',
            '所要時間の目安: 20分',
          ]} />
        </div>
        <div className="gm-card space-y-2 p-4">
          <h3 className="text-sm font-bold">知っておくこと</h3>
          <Checklist items={[
            '費用はかかりません。',
            '本文は X 向けに作った文をそのまま使います（Threads の上限は 500 字）。',
            '画像があれば一緒に投稿します。用意できないときは本文だけで投稿します。',
            '開発者サイトの表示が英語のときは、このページの（ ）内の名前を探してください。',
          ]} />
        </div>
      </div>

      <Steps>
        <Step n={1} title="Meta for Developers でアプリを作る"
          mock={
            <BrowserFrame url="developers.facebook.com/apps/creation">
              <div className="space-y-2">
                <div className="font-bold">アプリを作成</div>
                <MockInput label="アプリ名" value="genemo-post-threads" />
                <div className="text-[11px] text-ink-muted">ユースケースを追加</div>
                <MockRow><span>Facebook ログインで認証…</span><span className="ml-auto">☐</span></MockRow>
                <Mark n={1} block><MockRow><span className="font-bold">Threads API にアクセス</span><span className="ml-auto">☑</span></MockRow></Mark>
                <MockRow><span>Instagram でメッセージとコンテンツを管理</span><span className="ml-auto">☐</span></MockRow>
                <div className="flex justify-end"><MockButton kind="primary">次へ</MockButton></div>
              </div>
            </BrowserFrame>
          }>
          <p>
            <Code>developers.facebook.com/apps</Code> を開き、<UiLabel en="Create App">アプリを作成</UiLabel> を押します。
          </p>
          <ol className="list-decimal space-y-1 pl-5">
            <li>アプリ名（自分で分かれば何でも可）と連絡先メールを入れます。</li>
            <li>ユースケースで <UiLabel en="Access the Threads API">Threads API にアクセス</UiLabel> にチェックを入れます。</li>
            <li>ビジネスポートフォリオは <UiLabel en="I don't want to connect a business portfolio yet.">まだ接続しない</UiLabel> で構いません。</li>
            <li>確認画面で <UiLabel en="Create app">アプリを作成</UiLabel> を押します。</li>
          </ol>
        </Step>

        <Step n={2} title="投稿の権限（threads_content_publish）を追加する"
          mock={
            <BrowserFrame url="developers.facebook.com/apps/…/use_cases/customize">
              <MockSidebarLayout menu={['アクセス許可と機能', '設定']} active="アクセス許可と機能">
                <div className="font-bold">Threads API にアクセス</div>
                <MockRow><Code>threads_basic</Code><span className="ml-auto text-ok">✓ 追加済み</span></MockRow>
                <Mark n={2} block><MockRow><Code>threads_content_publish</Code><span className="ml-auto"><MockButton>追加</MockButton></span></MockRow></Mark>
                <MockRow><Code>threads_manage_insights</Code><span className="ml-auto"><MockButton>追加</MockButton></span></MockRow>
              </MockSidebarLayout>
            </BrowserFrame>
          }>
          <p>
            左のメニューの <UiLabel en="Use cases">ユースケース</UiLabel> → <b>Threads API にアクセス</b> の <UiLabel en="Customize">カスタマイズ</UiLabel> を開きます。
          </p>
          <p>
            <UiLabel en="Permissions and features">アクセス許可と機能</UiLabel> で、<Code>threads_content_publish</Code> の <UiLabel en="Add">追加</UiLabel> を押します。
            <Code>threads_basic</Code> は最初から入っています。
          </p>
          <Callout kind="warn">
            これを忘れると、トークンを貼ったときに「このトークンには投稿に必要な権限がありません」と断られます。
          </Callout>
        </Step>

        <Step n={3} title="自分の Threads アカウントを「Threads テスター」に招待する"
          mock={
            <BrowserFrame url="developers.facebook.com/apps/…/roles">
              <MockSidebarLayout menu={META_MENU} active="アプリの役割">
                <div className="flex items-center"><span className="font-bold">役割</span><span className="ml-auto"><MockButton>利用者を追加</MockButton></span></div>
                <div className="space-y-1.5 rounded-md border border-line p-2">
                  <div className="font-bold">アプリに利用者を追加</div>
                  <div>○ 管理者　○ 開発者　○ テスター</div>
                  <Mark n={3} block><div className="px-1 font-bold">● Threads テスター</div></Mark>
                  <MockInput label="Threads のユーザー名" value="genemo_coffee" />
                  <div className="flex justify-end"><MockButton kind="primary">追加</MockButton></div>
                </div>
              </MockSidebarLayout>
            </BrowserFrame>
          }>
          <p>
            同じ <UiLabel en="Customize use case">カスタマイズ</UiLabel> 画面の左で <UiLabel en="Settings">設定</UiLabel> を開き、
            いちばん下の <UiLabel en="User Token Generator">ユーザートークン生成ツール</UiLabel> にある
            <UiLabel en="Add or Remove Threads Testers">Threads テスターを追加・削除</UiLabel> を押します。
          </p>
          <p>
            <UiLabel en="App roles">アプリの役割</UiLabel> の画面に移るので、<UiLabel en="Add People">利用者を追加</UiLabel> →
            <UiLabel en="Threads Tester">Threads テスター</UiLabel> を選び、自分の Threads ユーザー名を入れて <UiLabel en="Add">追加</UiLabel> を押します。
          </p>
        </Step>

        <Step n={4} title="Threads で招待を承認する"
          mock={
            <PhoneFrame title="ウェブサイトのアクセス許可">
              <div className="flex gap-3 border-b border-line pb-1.5 text-[12px]">
                <span className="text-ink-muted">有効</span><span className="text-ink-muted">期限切れ</span>
                <span className="border-b-2 border-ink font-bold">招待</span>
              </div>
              <div className="flex items-center gap-2 rounded-md border border-line p-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-md bg-surface-2 text-[11px] font-bold">App</span>
                <div className="min-w-0 flex-1"><div className="truncate font-bold">genemo-post-threads</div><div className="text-[11px] text-ink-muted">テスターへの招待</div></div>
                <Mark n={4}><MockButton kind="primary">承認</MockButton></Mark>
              </div>
            </PhoneFrame>
          }>
          <p>
            招待した Threads アカウントで Threads を開き、<UiLabel en="Settings">設定</UiLabel> → <UiLabel en="Account">アカウント</UiLabel> →
            <UiLabel en="Website permissions">ウェブサイトのアクセス許可</UiLabel> → <UiLabel en="Invites">招待</UiLabel> を開きます。
            （パソコンの Web 版では、左下のメニュー → <UiLabel en="Settings">設定</UiLabel> から同じ場所に行けます。）
          </p>
          <p>手順1で作ったアプリの名前があるので <UiLabel en="Accept">承認</UiLabel> を押します。</p>
          <Callout kind="tip">
            招待が見つからないときは、Threads のアカウントが <b>公開</b> になっているか、手順3でユーザー名を間違えていないかを確かめてください。
          </Callout>
        </Step>

        <Step n={5} title="長期アクセストークンを発行してコピーする"
          mock={
            <BrowserFrame url="developers.facebook.com/apps/…/use_cases/customize/settings">
              <div className="space-y-2">
                <div className="font-bold">ユーザートークン生成ツール</div>
                <MockRow>
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-surface-2 text-[10px] font-bold">G</span>
                  <span>genemo_coffee</span>
                  <span className="ml-auto"><Mark n={5}><MockButton>アクセストークンを生成</MockButton></Mark></span>
                </MockRow>
                <div className="space-y-1.5 rounded-md border border-line-strong p-2 shadow-sm">
                  <div className="font-bold">アクセストークン</div>
                  <div className="truncate rounded bg-surface-2 px-2 py-1 font-mono text-[11px]">THAAG…（長い英数字）…</div>
                  <div>☑ 理解しました</div>
                  <div className="flex justify-end"><Mark n={6}><MockButton kind="primary">コピー</MockButton></Mark></div>
                </div>
              </div>
            </BrowserFrame>
          }>
          <p>
            開発者サイトに戻り、<UiLabel en="Use cases">ユースケース</UiLabel> → <b>Threads API にアクセス</b> → <UiLabel en="Customize">カスタマイズ</UiLabel> →
            <UiLabel en="Settings">設定</UiLabel> の <UiLabel en="User Token Generator">ユーザートークン生成ツール</UiLabel> を開きます。
          </p>
          <ol className="list-decimal space-y-1 pl-5">
            <li>自分のアカウントの行の <UiLabel en="Generate Access Token">アクセストークンを生成</UiLabel> を押します。</li>
            <li>Threads の許可画面で、アカウントが正しいことを確かめて <UiLabel en="Continue as …">〇〇として続行</UiLabel> を押します。</li>
            <li>戻った画面で <UiLabel en="I understand">理解しました</UiLabel> にチェックを入れ、<UiLabel en="Copy">コピー</UiLabel> を押します。</li>
          </ol>
          <Callout kind="tip" title="このトークンはそのまま使えます">
            ここで出るトークンは、最初から <b>60日有効の長期トークン</b> です。ほかの解説にある「短期トークンを長期トークンに交換する」作業は要りません。
          </Callout>
          <Callout kind="secret">トークンは、アカウントに投稿できる鍵です。他人に見せたり、チャットやメールに貼ったりしないでください。</Callout>
        </Step>

        <Step n={6} title="Genemo Post に貼って接続する"
          mock={
            <div className="space-y-3">
              <ConnectButtonsMock highlight="Threads を接続（トークン）" n={7} />
              <ConnectFormMock n={8} note="Meta for Developers で作ったアプリの「Threads API」で発行した長期アクセストークン（60日有効）を貼ります。…"
                fields={[{ label: '長期アクセストークン', value: 'THAAGxxxxxxxxxxxxxxxxxxxx', secret: true }]} />
            </div>
          }>
          <p>
            Genemo Post の <UiLabel>ペルソナ</UiLabel> で、投稿させたいペルソナの <UiLabel>Threads を接続（トークン）</UiLabel> を押します。
          </p>
          <p>
            <UiLabel>長期アクセストークン</UiLabel> の欄に貼り、<UiLabel>接続する</UiLabel> を押します。
            Genemo Post が Threads に問い合わせて、持ち主・期限・投稿の権限をその場で確かめます。
          </p>
        </Step>

        <Step n={7} title="「接続中」になれば完了"
          mock={<ChannelRowMock platform="Threads" handle="genemo_coffee" extra="トークンを入れ直す" />}>
          <p>Threads の行に <span className="gm-chip gm-chip-ok">接続中</span> と、ユーザー名が出れば完了です。</p>
          <p>
            トークンは期限が近づくと自動で延長されます。もし <span className="gm-chip gm-chip-bad">認証エラー</span> になったら、
            手順5でトークンを作り直し、行の <UiLabel>トークンを入れ直す</UiLabel> から貼ってください。チャンネルを作り直さないので、予約済みの投稿はそのまま出せます。
          </p>
        </Step>
      </Steps>

      <SectionTitle>うまくいかないとき</SectionTitle>
      <Trouble items={[
        { symptom: '「このトークンには投稿に必要な権限がありません（不足: threads_content_publish）」', fix: '手順2の権限の追加を忘れています。追加したあと、手順5でトークンを作り直して貼ってください（追加前に作ったトークンには権限が入っていません）。' },
        { symptom: '「このトークンは有効期限が24時間以内です」', fix: '1時間だけ有効な短期トークンを貼っています。手順5のユーザートークン生成ツールで作ったトークンを貼ってください。' },
        { symptom: '「Threads のトークンが無効です」', fix: 'コピーの途中で切れているか、トークンが取り消されています。手順5からやり直してください。' },
        { symptom: '「この Threads アカウント（…）はすでに接続されています」', fix: '同じアカウントを二重に接続しようとしています。既にある行の「トークンを入れ直す」を使ってください。' },
        { symptom: 'ユーザートークン生成ツールに自分のアカウントが出ない', fix: '手順4の招待の承認がまだです。Threads の「ウェブサイトのアクセス許可」→「招待」を確かめてください。' },
      ]} />
    </div>
  )
}
